import schema from 'fargate-deployer/schema/manifest.schema.json';

/**
 * The manifest reference, generated from the published JSON Schema.
 *
 * The schema ships inside the npm package, so the reference on this page is
 * whatever the installed version actually enforces. Writing the field list by
 * hand would guarantee it drifts the first time a field is added.
 */

export interface Field {
  /** Dotted path, e.g. `loadBalancer.healthCheck.path`. */
  path: string;
  /** Nesting depth, for indentation. */
  depth: number;
  type: string;
  required: boolean;
  description?: string;
  /** Human-readable constraint summary: allowed values, ranges, patterns. */
  constraint?: string;
}

export interface Group {
  /** Top-level manifest key, e.g. `task`. */
  key: string;
  description?: string;
  required: boolean;
  fields: Field[];
}

type Node = Record<string, any>;

export function manifestGroups(): Group[] {
  const root = schema as Node;
  const required: string[] = root.required ?? [];

  return Object.entries(root.properties as Record<string, Node>).map(([key, node]) => ({
    key,
    description: node.description,
    required: required.includes(key),
    fields: walk(node, key, 0, required.includes(key)),
  }));
}

function walk(node: Node, path: string, depth: number, required: boolean): Field[] {
  const self: Field = {
    path,
    depth,
    type: typeName(node),
    required,
    description: node.description,
    constraint: constraintOf(node),
  };

  // Objects and arrays-of-objects expand into their children.
  const object = node.type === 'object' ? node : node.type === 'array' ? node.items : undefined;
  if (!object?.properties) {
    return [self];
  }

  const childRequired: string[] = object.required ?? [];
  const children = Object.entries(object.properties as Record<string, Node>).flatMap(
    ([childKey, childNode]) =>
      walk(childNode, `${path}${node.type === 'array' ? '[].' : '.'}${childKey}`, depth + 1,
        childRequired.includes(childKey)),
  );

  return [self, ...children];
}

function typeName(node: Node): string {
  if (node.enum) {
    return node.enum.every((v: unknown) => typeof v === 'number') ? 'number' : 'string';
  }
  if (node.oneOf || node.anyOf) {
    const variants = (node.oneOf ?? node.anyOf) as Node[];
    return [...new Set(variants.map(typeName))].join(' | ');
  }
  if (node.type === 'array') {
    return `${typeName(node.items ?? {})}[]`;
  }
  if (node.type === 'object' && node.additionalProperties?.type) {
    return `map<string, ${node.additionalProperties.type}>`;
  }
  return node.type ?? 'any';
}

/** Turn the validation keywords into something readable. */
function constraintOf(node: Node): string | undefined {
  const parts: string[] = [];

  if (node.enum) {
    const values = node.enum as unknown[];
    parts.push(
      values.length > 8
        ? `one of ${values.slice(0, 8).join(', ')} … (${values.length} values)`
        : `one of ${values.join(', ')}`,
    );
  }
  if (node.minimum !== undefined && node.maximum !== undefined) {
    parts.push(`${node.minimum}–${node.maximum}`);
  } else if (node.minimum !== undefined) {
    parts.push(`≥ ${node.minimum}`);
  } else if (node.maximum !== undefined) {
    parts.push(`≤ ${node.maximum}`);
  }
  if (node.minItems) parts.push(`at least ${node.minItems} item${node.minItems === 1 ? '' : 's'}`);
  if (node.maxLength) parts.push(`≤ ${node.maxLength} characters`);
  if (node.pattern) parts.push(`matches \`${node.pattern}\``);
  if (node.default !== undefined) parts.push(`defaults to ${JSON.stringify(node.default)}`);

  return parts.length ? parts.join(' · ') : undefined;
}
